import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { useMemo } from 'react';
import type { Difficulty, ProblemSummary } from '@flarecode/shared';
import { DIFFICULTIES } from '@flarecode/shared';
import { api } from '../lib/api';
import { useCurrentUser } from '../hooks/useCurrentUser';

/**
 * Client-side filtering over the full problem list.
 *
 * We filter in the browser because:
 *   - The list fits comfortably in memory even at 100-500 problems
 *   - Filter feedback is instant (no server round-trip)
 *   - Shareable URL state via query params
 *
 * Filter state is mirrored to the URL so that URLs like
 * `/problems?d=easy&t=array&s=unsolved` can be shared and bookmarked.
 *
 * Query params:
 *   q = title substring
 *   d = comma-separated difficulties (subset of easy,medium,hard)
 *   t = comma-separated tags (intersection semantics)
 *   s = solved status filter ('solved' | 'unsolved'); omitted = all
 *
 * The Status column + status filter only render for authenticated users
 * (the API only populates `solvedByUser` when the caller is authed).
 */

type StatusFilter = 'all' | 'solved' | 'unsolved';

export function ProblemListPage() {
  const { user } = useCurrentUser();

  const { data, isLoading, error } = useQuery({
    queryKey: ['problems'],
    queryFn: () => api.listProblems(),
  });

  const [searchParams, setSearchParams] = useSearchParams();

  const query = (searchParams.get('q') ?? '').toLowerCase();
  const difficultyFilter = parseDifficulties(searchParams.get('d'));
  const tagFilter = parseTags(searchParams.get('t'));
  const statusFilter = parseStatus(searchParams.get('s'));

  const problems = data?.problems ?? [];

  const allTags = useMemo(() => {
    const set = new Set<string>();
    for (const p of problems) for (const t of p.tags) set.add(t);
    return [...set].sort();
  }, [problems]);

  const filtered = useMemo(
    () => filterProblems(problems, query, difficultyFilter, tagFilter, statusFilter),
    [problems, query, difficultyFilter, tagFilter, statusFilter],
  );

  function updateParam(key: string, value: string | null) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value === null || value === '') next.delete(key);
        else next.set(key, value);
        return next;
      },
      { replace: true },
    );
  }

  function toggleDifficulty(d: Difficulty) {
    const next = new Set(difficultyFilter);
    if (next.has(d)) next.delete(d);
    else next.add(d);
    updateParam('d', next.size === 0 ? null : [...next].join(','));
  }

  function toggleTag(t: string) {
    const next = new Set(tagFilter);
    if (next.has(t)) next.delete(t);
    else next.add(t);
    updateParam('t', next.size === 0 ? null : [...next].join(','));
  }

  function setStatus(next: StatusFilter) {
    updateParam('s', next === 'all' ? null : next);
  }

  function clearFilters() {
    setSearchParams({}, { replace: true });
  }

  if (isLoading) return <p>Loading…</p>;
  if (error) return <p className="error">Failed to load problems.</p>;

  const hasActiveFilters =
    query.length > 0 || difficultyFilter.size > 0 || tagFilter.size > 0 || statusFilter !== 'all';

  // Only render the Status column / filter for authenticated users. For
  // anonymous callers the API doesn't populate `solvedByUser`, so there's
  // nothing to show.
  const showStatus = !!user;

  return (
    <section>
      <div className="problem-list-header">
        <h2>Problems</h2>
        <span className="problem-list-count">
          {filtered.length} of {problems.length}
        </span>
      </div>

      {problems.length === 0 ? (
        <p className="empty">
          No problems yet. Seed some with <code>pnpm run seed:upload</code>.
        </p>
      ) : (
        <>
          <div className="problem-filters">
            <input
              type="search"
              placeholder="Search by title…"
              value={searchParams.get('q') ?? ''}
              onChange={(e) => updateParam('q', e.target.value || null)}
              className="filter-search"
              aria-label="Search problems by title"
            />

            {showStatus && (
              <div className="filter-group">
                <span className="filter-label">Status:</span>
                {(['all', 'solved', 'unsolved'] as const).map((s) => {
                  const active = statusFilter === s;
                  return (
                    <button
                      key={s}
                      type="button"
                      className={`status-chip ${active ? 'chip--active' : 'chip--inactive'}`}
                      onClick={() => setStatus(s)}
                      aria-pressed={active}
                    >
                      {s === 'all' ? 'All' : s === 'solved' ? 'Solved' : 'Unsolved'}
                    </button>
                  );
                })}
              </div>
            )}

            <div className="filter-group">
              <span className="filter-label">Difficulty:</span>
              {DIFFICULTIES.map((d) => {
                const active = difficultyFilter.has(d);
                return (
                  <button
                    key={d}
                    type="button"
                    className={`difficulty-chip difficulty--${d} ${
                      active ? 'chip--active' : 'chip--inactive'
                    }`}
                    onClick={() => toggleDifficulty(d)}
                    aria-pressed={active}
                  >
                    {d}
                  </button>
                );
              })}
            </div>

            {allTags.length > 0 && (
              <div className="filter-group">
                <span className="filter-label">Tags:</span>
                {allTags.map((t) => {
                  const active = tagFilter.has(t);
                  return (
                    <button
                      key={t}
                      type="button"
                      className={`tag-chip ${active ? 'chip--active' : 'chip--inactive'}`}
                      onClick={() => toggleTag(t)}
                      aria-pressed={active}
                    >
                      {t}
                    </button>
                  );
                })}
              </div>
            )}

            {hasActiveFilters && (
              <button type="button" className="linklike" onClick={clearFilters}>
                Clear filters
              </button>
            )}
          </div>

          {filtered.length === 0 ? (
            <p className="empty">No problems match your filters.</p>
          ) : (
            <table className="problem-list">
              <thead>
                <tr>
                  {showStatus && <th className="status-col-head">Status</th>}
                  <th>Title</th>
                  <th>Difficulty</th>
                  <th>Tags</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => (
                  <tr key={p.id}>
                    {showStatus && (
                      <td className="status-col-cell">
                        {p.solvedByUser ? (
                          <span
                            className="solved-indicator"
                            aria-label="Solved"
                            title="You have solved this problem"
                          >
                            ✓
                          </span>
                        ) : null}
                      </td>
                    )}
                    <td>
                      <Link to={`/problems/${p.slug}`}>{p.title}</Link>
                    </td>
                    <td className={`difficulty difficulty--${p.difficulty}`}>{p.difficulty}</td>
                    <td className="tag-cell">
                      {p.tags.map((t) => (
                        <span key={t} className="tag-chip">
                          {t}
                        </span>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </section>
  );
}

// ---------- helpers ----------

function filterProblems(
  problems: ProblemSummary[],
  query: string,
  difficulties: Set<Difficulty>,
  tags: Set<string>,
  status: StatusFilter,
): ProblemSummary[] {
  return problems.filter((p) => {
    if (query && !p.title.toLowerCase().includes(query)) return false;
    if (difficulties.size > 0 && !difficulties.has(p.difficulty)) return false;
    if (tags.size > 0) {
      // A problem matches the tag filter if it has EVERY selected tag.
      // Intersection semantics: "show me problems tagged both 'array' AND 'dp'".
      for (const t of tags) if (!p.tags.includes(t)) return false;
    }
    if (status === 'solved' && !p.solvedByUser) return false;
    if (status === 'unsolved' && p.solvedByUser) return false;
    return true;
  });
}

function parseDifficulties(param: string | null): Set<Difficulty> {
  if (!param) return new Set();
  const out = new Set<Difficulty>();
  for (const piece of param.split(',')) {
    const trimmed = piece.trim();
    if ((DIFFICULTIES as readonly string[]).includes(trimmed)) {
      out.add(trimmed as Difficulty);
    }
  }
  return out;
}

function parseTags(param: string | null): Set<string> {
  if (!param) return new Set();
  const out = new Set<string>();
  for (const piece of param.split(',')) {
    const trimmed = piece.trim();
    if (trimmed) out.add(trimmed);
  }
  return out;
}

function parseStatus(param: string | null): StatusFilter {
  if (param === 'solved' || param === 'unsolved') return param;
  return 'all';
}
